import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {apiClient} from 'common/util/api-client';
import {promisifyModal} from 'common/components/modal/utils';
import {IronContextRegistry} from 'common/report/context-registry';

import './dataset';
import css from './index.inline.styl';
import './dataset/index.styl';
import template from './index.jade';

export const DemoProjectListEndpoint = `/api/app/demo_project/list`;
export const DemoProjectJoinEndpoint = `/api/app/demo_project/join`;

export interface DemoProject {
  icon: string;
  description: string;
  projectId: number;
}

interface DemoSelectorState {
  demoProjects: DemoProject[];
  loadingProjects: boolean;
  selectedDemo: number;
  waitingOnResponse: boolean;
}
interface DemoSelectorAttrs {
  'is-fyt': boolean;
  'owner-email': string;
}
interface JoinDemoProjectResult {
  success: boolean;
  error: string;
  redirect_url: string;
}

export interface ListDemoProjectResult extends Omit<DemoProject, 'projectId'> {
  project_id: number;
}

export class DemoProjectSelector extends Component<
  DemoSelectorState,
  DemoSelectorAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<DemoSelectorAttrs> {
    return {
      'is-fyt': {type: `boolean`, description: `from fyt page or not`},
      'owner-email': {type: `string`, description: `org owner email for fyt`},
    };
  }

  get config(): ConfigOptions<DemoSelectorState, unknown, IronContextRegistry> {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        demoProjects: [],
        loadingProjects: true,
        selectedDemo: null,
        waitingOnResponse: false,
      },
      contexts: [`urlManager` as const],
      helpers: {
        clickedDataset: (projectId) => this.update({selectedDemo: projectId}),
        clickedExploreDemoButton: (): void => {
          this.update({waitingOnResponse: true});
          this.joinDemoProject().then(() => this.update({waitingOnResponse: false}));
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.listDemoProjects().then((demoProjects) => this.update({demoProjects, loadingProjects: false}));
  }
  async listDemoProjects(): Promise<DemoProject[]> {
    const demoProjects: DemoProject[] = [];
    const demoProjectsResult = await apiClient.get<ListDemoProjectResult[]>(DemoProjectListEndpoint);
    demoProjectsResult.forEach((project) =>
      demoProjects.push({projectId: project.project_id, ...project} as DemoProject),
    );
    return demoProjects;
  }
  async joinDemoProject() {
    try {
      const result = await apiClient.post<JoinDemoProjectResult>(DemoProjectJoinEndpoint, {
        project_id: this.state.selectedDemo,
      });
      this.getContext(`urlManager`).setLocation(`/report/${this.state.selectedDemo}${result.redirect_url}`);
    } catch (err) {
      this.handleError(err.message);
    }
  }
  handleError(message: string): void {
    this.dispatchEvent(
      new CustomEvent(`error`, {
        detail: {
          message,
        },
      }),
    );
  }
}

export const DemoSelectorModal = defineAndMakeJsxFactory(`mp-demo-selector-modal`, DemoProjectSelector);
export const DemoSelectorModalReact = panel2react(`mp-demo-selector-modal`, DemoProjectSelector);

export const openDemoProjectSelectorModal = (onErrorMessage: (ev: CustomEvent) => void, attrs = {}) => {
  return promisifyModal({
    tagName: `mp-demo-selector-modal`,
    attrs,
    closeEvents: [`error`],
    closeHandler: (ev) => {
      if (ev.type === `error`) {
        onErrorMessage(ev);
      }
    },
  });
};
